import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { 
  Eye, EyeOff, ArrowRight, ExternalLink,
  BarChart3, FileText, Package, Users, Store, TrendingUp,
  ShoppingCart, Wallet, CheckCircle2, LogIn,
  ArrowUpRight, Sparkles, Shield, Clock
} from 'lucide-react';

/* ================================================================
   STYLES — keyframes + animations injectées via <style> dans le JSX
   ================================================================ */
const portalStyles = `
  @keyframes floatSlow {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(3deg); }
  }
  @keyframes floatMedium {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-14px) rotate(-2deg); }
  }
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.92); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes pulse-ring {
    0% { transform: scale(1); opacity: 0.4; }
    100% { transform: scale(1.8); opacity: 0; }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes countUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  .anim-float-slow { animation: floatSlow 6s ease-in-out infinite; }
  .anim-float-med  { animation: floatMedium 5s ease-in-out infinite; }
  .anim-gradient    { background-size: 200% 200%; animation: gradientShift 8s ease infinite; }
  .anim-slide-up    { animation: slideUp 0.7s ease-out both; }
  .anim-slide-up-2  { animation: slideUp 0.7s ease-out 0.15s both; }
  .anim-slide-up-3  { animation: slideUp 0.7s ease-out 0.3s both; }
  .anim-scale-in    { animation: scaleIn 0.6s ease-out 0.2s both; }
  .anim-count       { animation: countUp 0.5s ease-out both; }
  .anim-shimmer     { background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent); background-size: 200% 100%; animation: shimmer 2s infinite; }
`;

/* ================================================================
   GREETING dynamique selon l'heure
   ================================================================ */
const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return { text: 'Bonjour', emoji: '☀️' };
  if (h < 18) return { text: 'Bon après-midi', emoji: '🌤️' };
  return { text: 'Bonsoir', emoji: '🌙' };
};

/* ================================================================
   LIVE STATS CARD — mini-composant animé
   ================================================================ */
const LiveStatCard = ({ icon: Icon, label, value, change, color, delay }) => {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div className={`relative bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-white/60 shadow-lg shadow-${color}-100/40 transition-all duration-700 hover:scale-[1.03] hover:shadow-xl ${show ? 'anim-count' : 'opacity-0'}`}>
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-${color}-400 to-${color}-600 flex items-center justify-center text-white shadow-md shadow-${color}-300/50 mb-3`}>
        <Icon size={20} />
      </div>
      <div className="text-2xl font-extrabold text-gray-800 tracking-tight">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      {change && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-semibold text-${color}-600`}>
          <ArrowUpRight size={12} />
          <span>{change}</span>
        </div>
      )}
    </div>
  );
};

/* ================================================================
   FEATURE CAROUSEL — défile automatiquement
   ================================================================ */
const featureList = [
  { icon: BarChart3, title: 'Tableau de bord', desc: 'Vue d\'ensemble en temps réel', gradient: 'from-teal-500 to-cyan-500' },
  { icon: FileText, title: 'Factures & Devis', desc: 'Créez en quelques clics', gradient: 'from-blue-500 to-indigo-500' },
  { icon: Package, title: 'Gestion de stock', desc: 'Suivi intelligent des produits', gradient: 'from-amber-500 to-orange-500' },
  { icon: Users, title: 'Base clients', desc: 'CRM intégré et puissant', gradient: 'from-pink-500 to-rose-500' },
  { icon: Store, title: 'Boutique en ligne', desc: 'Vendez partout, tout le temps', gradient: 'from-violet-500 to-purple-500' },
  { icon: TrendingUp, title: 'Rapports', desc: 'Analyses et statistiques', gradient: 'from-emerald-500 to-teal-500' },
];

const CAROUSEL_INTERVAL = 3500;

const FeatureCarousel = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward

  // Auto-advance with progress bar
  useEffect(() => {
    if (paused) { setProgress(0); return; }
    const step = 50; // ms per tick
    const ticks = CAROUSEL_INTERVAL / step;
    let tick = 0;
    const t = setInterval(() => {
      tick++;
      setProgress((tick / ticks) * 100);
      if (tick >= ticks) {
        setDirection(1);
        setActive(p => (p + 1) % featureList.length);
        tick = 0;
      }
    }, step);
    return () => clearInterval(t);
  }, [paused, active]);

  const goTo = (i) => {
    setDirection(i > active ? 1 : -1);
    setActive(i);
    setProgress(0);
  };

  const prev = () => { setDirection(-1); setActive(p => (p - 1 + featureList.length) % featureList.length); setProgress(0); };
  const next = () => { setDirection(1); setActive(p => (p + 1) % featureList.length); setProgress(0); };

  const f = featureList[active];
  const Icon = f.icon;

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Main card */}
      <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-white/60 shadow-lg shadow-gray-200/40 overflow-hidden">
        {/* Progress bar */}
        <div className="h-0.5 bg-gray-100">
          <div
            className="h-full bg-gradient-to-r from-teal-400 to-emerald-400 transition-[width] duration-100 ease-linear"
            style={{ width: `${paused ? 100 : progress}%` }}
          />
        </div>

        <div className="p-5 flex items-start gap-4">
          {/* Icon */}
          <div
            key={`icon-${active}-${direction}`}
            className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center text-white shadow-lg flex-shrink-0`}
            style={{ animation: 'scaleIn 0.35s ease-out both' }}
          >
            <Icon size={22} />
          </div>

          {/* Text */}
          <div
            key={`text-${active}-${direction}`}
            className="flex-1 min-w-0"
            style={{ animation: `slideUp 0.35s ease-out both` }}
          >
            <div className="text-[15px] font-bold text-gray-800 mb-0.5">{f.title}</div>
            <div className="text-[13px] text-gray-500 leading-snug">{f.desc}</div>
          </div>

          {/* Nav arrows */}
          <div className="flex items-center gap-1 flex-shrink-0 mt-1">
            <button
              onClick={prev}
              className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all"
              aria-label="Précédent"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button
              onClick={next}
              className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all"
              aria-label="Suivant"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </div>
      </div>

      {/* Feature pills row — mini navigation */}
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {featureList.map((feat, i) => {
          const FIcon = feat.icon;
          const isCurrent = i === active;
          return (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all duration-300 border ${
                isCurrent
                  ? 'bg-white shadow-md border-teal-200/80 text-teal-700 scale-[1.03]'
                  : 'bg-white/40 border-transparent text-gray-400 hover:bg-white/70 hover:text-gray-600'
              }`}
            >
              <FIcon size={12} />
              <span>{feat.title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ================================================================
   PAGE PRINCIPALE
   ================================================================ */
const WorkspacePortalLogin = () => {
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [mot_de_passe, setMotDePasse] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formFocused, setFormFocused] = useState(false);
  const navigate = useNavigate();
  const greeting = getGreeting();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, mot_de_passe);
      navigate('/portal/workspace');
    } catch (err) {
      if (err.emailNotVerified) {
        setError('Veuillez vérifier votre email avant de vous connecter.');
      } else {
        setError(err.response?.data?.error || 'Identifiants incorrects');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{portalStyles}</style>
      <div className="min-h-screen relative overflow-hidden">

        {/* ===== ANIMATED BACKGROUND ===== */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-cyan-50/50 to-emerald-50 anim-gradient"></div>
        
        {/* Floating orbs */}
        <div className="absolute top-20 left-[10%] w-72 h-72 bg-teal-200/30 rounded-full blur-3xl anim-float-slow"></div>
        <div className="absolute bottom-20 right-[5%] w-96 h-96 bg-emerald-200/25 rounded-full blur-3xl anim-float-med"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-100/20 rounded-full blur-3xl"></div>
        <div className="absolute top-10 right-[20%] w-48 h-48 bg-amber-100/30 rounded-full blur-2xl anim-float-med" style={{ animationDelay: '1s' }}></div>
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #0d9488 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

        {/* ===== CONTENT ===== */}
        <div className="relative z-10 min-h-screen flex flex-col">

          {/* ===== HEADER ===== */}
          <header className="flex items-center justify-between px-6 md:px-10 py-5 anim-slide-up">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img src="/logo.png" alt="DJAMANN" className="w-11 h-11 rounded-xl object-contain shadow-lg" />

              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-extrabold text-gray-800 tracking-tight">DJAMANN</span>
                  <span className="text-[10px] font-bold bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-2.5 py-0.5 rounded-lg uppercase tracking-widest shadow-sm">Workspace</span>
                </div>
                <p className="text-[11px] text-gray-400 font-medium">Votre espace professionnel tout-en-un</p>
              </div>
            </div>
            <Link to="/login" className="hidden sm:flex items-center gap-2 text-[13px] text-gray-400 hover:text-teal-600 transition-all bg-white/60 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/60 hover:border-teal-200 hover:bg-white/80 group">
              <span>Aller sur DJAMANN</span>
              <ExternalLink size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </header>

          {/* ===== MAIN CONTENT ===== */}
          <main className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-10 xl:gap-16 px-6 md:px-10 py-6 lg:py-0">

            {/* ---- LEFT SIDE: Hero + Live Stats + Features ---- */}
            <div className="flex-1 max-w-xl w-full">
              
              {/* Greeting badge */}
              <div className="anim-slide-up inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-white/60 rounded-full px-4 py-2 shadow-sm mb-6">
                <span className="text-lg">{greeting.emoji}</span>
                <span className="text-sm font-semibold text-gray-700">{greeting.text} !</span>
                <Sparkles size={14} className="text-amber-400" />
              </div>

              {/* Headline */}
              <div className="anim-slide-up-2 mb-8">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-[1.1] tracking-tight mb-5">
                  Votre business{' '}
                  <span className="relative">
                    <span className="bg-gradient-to-r from-teal-500 via-cyan-500 to-emerald-500 bg-clip-text text-transparent anim-gradient" style={{ backgroundSize: '200% 200%' }}>mérite le meilleur</span>
                  </span>
                  <br />
                  <span className="text-gray-400 text-2xl sm:text-3xl lg:text-4xl font-bold">outil de gestion.</span>
                </h1>
                <p className="text-gray-500 text-base lg:text-lg leading-relaxed max-w-md">
                  Pilotez vos ventes, factures, stocks et clients depuis un 
                  <span className="font-semibold text-gray-700"> tableau de bord unique</span> — conçu pour les entrepreneurs.
                </p>
              </div>


              {/* Feature carousel — desktop only */}
              <div className="hidden lg:block anim-slide-up-3">
                <FeatureCarousel />
              </div>
            </div>

            {/* ---- RIGHT SIDE: Login Card ---- */}
            <div className="w-full max-w-[420px] flex-shrink-0 anim-scale-in">
              <div className={`relative bg-white/90 backdrop-blur-xl rounded-3xl border shadow-2xl overflow-hidden transition-all duration-500 ${formFocused ? 'border-teal-200 shadow-teal-100/50' : 'border-white/60 shadow-gray-200/40'}`}>
                
                {/* Shimmer bar */}
                <div className="h-1 bg-gradient-to-r from-teal-400 via-emerald-400 to-cyan-400 relative overflow-hidden">
                  <div className="absolute inset-0 anim-shimmer"></div>
                </div>

                <div className="p-7 sm:p-9">
                  {/* Welcome section */}
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-teal-300/40">
                        <LogIn size={22} />
                      </div>
                      <div>
                        <h2 className="text-xl font-extrabold text-gray-900">Connexion</h2>
                        <p className="text-xs text-gray-400 font-medium">Accédez à votre espace de travail</p>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-2xl text-[13px] text-red-600 flex items-center gap-2.5" style={{ animation: 'slideUp 0.3s ease-out' }}>
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-red-500 text-xs font-bold">!</span>
                      </div>
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-2">Adresse email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setFormFocused(true)}
                        onBlur={() => setFormFocused(false)}
                        className="w-full px-4 py-3.5 bg-gray-50/80 border border-gray-200 rounded-xl text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-4 focus:ring-teal-100/60 focus:bg-white transition-all"
                        placeholder="nom@entreprise.com"
                        required
                        autoComplete="email"
                      />
                    </div>

                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-2">Mot de passe</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={mot_de_passe}
                          onChange={(e) => setMotDePasse(e.target.value)}
                          onFocus={() => setFormFocused(true)}
                          onBlur={() => setFormFocused(false)}
                          className="w-full px-4 py-3.5 pr-12 bg-gray-50/80 border border-gray-200 rounded-xl text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-4 focus:ring-teal-100/60 focus:bg-white transition-all"
                          placeholder="Votre mot de passe"
                          required
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-teal-500 rounded-lg hover:bg-teal-50 transition-all"
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input type="checkbox" className="w-4 h-4 rounded-md border-gray-300 text-teal-500 focus:ring-teal-400 accent-teal-500" />
                        <span className="text-[13px] text-gray-500 group-hover:text-gray-700 transition-colors">Rester connecté</span>
                      </label>
                      <Link to="/forgot-password" className="text-[13px] text-teal-600 hover:text-teal-700 font-semibold hover:underline transition-all">
                        Mot de passe oublié ?
                      </Link>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-500 anim-gradient text-white font-bold rounded-xl hover:shadow-xl hover:shadow-teal-500/25 transition-all duration-300 disabled:opacity-40 flex items-center justify-center gap-2 text-[15px] active:scale-[0.98] relative overflow-hidden group"
                    >
                      {/* Hover shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                      ) : (
                        <>
                          <span className="relative">Accéder à mon Workspace</span>
                          <ArrowRight size={18} className="relative group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Divider */}
                  <div className="mt-7 pt-6 border-t border-gray-100 text-center">
                    <p className="text-[13px] text-gray-400">
                      Pas encore de compte ?{' '}
                      <Link to="/register" className="text-teal-600 font-bold hover:text-teal-700 hover:underline transition">
                        Créez-en un gratuitement
                      </Link>
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust badges under form */}
              <div className="flex items-center justify-center gap-5 mt-5 text-[11px] text-gray-400">
                <div className="flex items-center gap-1.5">
                  <Shield size={13} className="text-teal-500" />
                  <span>SSL Sécurisé</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock size={13} className="text-teal-500" />
                  <span>Accès 24/7</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-teal-500" />
                  <span>1200+ pros</span>
                </div>
              </div>

              {/* Back link */}
              <div className="mt-3 text-center">
                <Link to="/login" className="text-[12px] text-gray-400 hover:text-teal-600 transition-all">
                  ← Retour sur DJAMANN
                </Link>
              </div>
            </div>
          </main>

          {/* ===== MOBILE: Live stats + Features ===== */}
          <div className="lg:hidden px-6 pb-10 space-y-6">

            
            {/* Features */}
            <div className="flex flex-wrap justify-center gap-2">
              {featureList.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-white/60 rounded-full pl-1.5 pr-3.5 py-1.5 shadow-sm">
                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${f.gradient} flex items-center justify-center text-white`}>
                      <Icon size={13} />
                    </div>
                    <span className="text-[11px] font-medium text-gray-600">{f.title}</span>
                  </div>
                );
              })}
            </div>

            {/* Trust */}
            <div className="flex items-center justify-center gap-4 text-[11px] text-gray-400">
              <div className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /><span>Sécurisé</span></div>
              <div className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /><span>1200+ pros</span></div>
              <div className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /><span>Support réactif</span></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkspacePortalLogin;
